import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import ContentPageSlides from '../../components/ContentPages/ContentPageSlides';
import ContentFooter from '../../components/ContentPages/ContentFooter';
import ContentSidebar from '../../components/ContentPages/ContentSidebar';
import ProgressBar from '../../components/ContentPages/ProgressBar';
import SlideListSidebar from '../../components/ContentPages/SlideListSidebar';
import { slugifySectionName } from '../../utils/slugify';
import { getContentPage, getTestData, getContentPageFromRouter} from '../../services/dataService';
import { useData } from '../../state/DataContext'; 
import { useStudentProgress } from '../../state/StudentProgressContext';

function ContentPage() {
  const { courseType, sectionSlug } = useParams();
  const navigate = useNavigate();

  const { basicData, advancedData, loading, error: dataError } = useData(); 
  const { completeSection } = useStudentProgress();
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [submittedAnswers, setSubmittedAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [chevronDirection, setChevronDirection] = useState('left');
  const [error, setError] = useState(null);


  let sectionNumber = null;
  let sectionNameSlug = '';
  if (sectionSlug) {
    const match = sectionSlug.match(/^(\d+)-(.+)$/);
    if (match) {
      sectionNumber = parseInt(match[1], 10);
      sectionNameSlug = match[2];
    }
  }

  const chaptersData = courseType === "advanced" ? advancedData : basicData;

  let foundSection = null;
  let foundChapter = null;
  let sectionCounter = 1;
  let allSections = [];

  if (chaptersData) {
    chaptersData.forEach(chapter => {
      chapter.sections.forEach(section => {
        if (section.type === "section") {
          allSections.push({ ...section, chapterTitle: chapter.title });
        }
      });
    });
  
    outer: for (const chapter of chaptersData) {
      for (const section of chapter.sections) {
        if (section.type === "section") {
          const slug = slugifySectionName(section.name);
          if (sectionCounter === sectionNumber && slug === sectionNameSlug) {
            foundSection = section;
            foundChapter = chapter;
            break outer;
          }
          sectionCounter++;
        }
      }
    }
  }

  // Fetch slides (test data) when section changes
  useEffect(() => {
    setError(null);

    if (!foundSection) {
      setError("Invalid section path.");
      setSlides([]);
      return;
    }

    async function fetchSlides() {
      console.log(courseType, sectionNumber)
      const { data, error } = await getContentPageFromRouter(courseType, sectionNumber); // api
      // const { data, error } = await getTestData(); // test
      if (error) {
        setError(error);
      } else {
        setSlides(data);
        setSubmittedAnswers(Array(data.length).fill(null));
        setCurrentSlide(0);
        setSelectedOption(null);
        setIsLastSlide(data.length > 0 && 0 === data.length - 1);
      }
    }

    fetchSlides();
  }, [sectionSlug, courseType, foundSection]);

  useEffect(() => {
    setIsLastSlide(slides.length > 0 && currentSlide === slides.length - 1);
  }, [currentSlide, slides.length]);

  function handleSlideNavigation(slideIndex) {
    setCurrentSlide(slideIndex);
    setSelectedOption(null);
  }

  function handleNextSlide() {
    if (currentSlide < slides.length - 1) {
      handleSlideNavigation(currentSlide + 1);
    }
  }

  function handlePreviousSlide() {
    if (currentSlide > 0) {
      handleSlideNavigation(currentSlide - 1);
    }
  }

  function navigateTo(target, options = {}) {
    if (options.isBonus) {
      navigate(`/${courseType}?chapterId=${target}`);
    } else {
      navigate(`/${courseType}/${target}`);
    }
    // Close sidebar on navigation
    handleCloseLeftSidebar();
  }

  async function handleSectionComplete() {
    if (foundSection) {
      await completeSection(courseType, sectionNumber);
    }

    const currentSectionIndex = allSections.findIndex(s => s.section_id === foundSection.section_id);
    if (currentSectionIndex < allSections.length - 1) {
      const nextSection = allSections[currentSectionIndex + 1];
      const nextSectionSlug = `${String(currentSectionIndex + 2).padStart(2, '0')}-${slugifySectionName(nextSection.name)}`;
      navigate(`/${courseType}/${nextSectionSlug}`);
    } else {
      navigate(`/${courseType}`);
    }
  }

  function toggleRightSidebar() {
    setChevronDirection(dir => (dir === 'left' ? 'right' : 'left'));
    const offcanvasEl = document.getElementById('offcanvasScrolling');
    if (offcanvasEl) {
      window.bootstrap?.Offcanvas.getOrCreateInstance(offcanvasEl).toggle();
    }
  }

  function toggleLeftSidebar() {
    const offcanvasEl = document.getElementById('offcanvasLeft');
    if (offcanvasEl) {
      window.bootstrap?.Offcanvas.getOrCreateInstance(offcanvasEl).toggle();
    }
  }

  function handleCloseLeftSidebar() {
    const offcanvasEl = document.getElementById('offcanvasLeft');
    if (offcanvasEl) {
      const instance = window.bootstrap?.Offcanvas.getInstance(offcanvasEl);
      if (instance) {
        instance.hide();
      }
    }
  }

  let contextTitle = foundChapter ? foundChapter.title : '';
  let sectionIdx = 0;
  let totalSections = 0;

  if (foundChapter && foundSection) {
    // Only count "section" type, not "bonus" (bonus is not used for now)
    const sections = foundChapter.sections.filter(s => s.type === "section");
    totalSections = sections.length;
    sectionIdx = sections.findIndex(s => s.section_id === foundSection.section_id) + 1;
  }
  const contextSection = sectionIdx && totalSections
    ? `Section ${sectionIdx}/${totalSections}`
    : '';

  if (loading) return <div>Loading...</div>;
  if (dataError || error) return <div>{dataError || error}</div>;
  if (!slides.length) return <div>No content found.</div>;

  return (
    <div>
      <div className="content-container" style={{ display: 'flex', flexDirection: 'row', minHeight: '80vh' }}>
        <div className="container-fluid" id="content-section" style={{ flex: 1, maxWidth: '800px', margin: 'auto'}}>
          <div className={`content3slides-animated${chevronDirection === 'right' ? ' slide-left' : ''}`}>
            <ProgressBar current={currentSlide + 1} total={slides.length} />
            <ContentPageSlides
              slide={slides[currentSlide]}
              currentSlide={currentSlide}
              slideCount={slides.length}
              isLastSlide={isLastSlide}
              onPrev={handlePreviousSlide}
              onNext={handleNextSlide}
              submittedAnswers={submittedAnswers}
              setSubmittedAnswers={setSubmittedAnswers}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
            />
          </div>
        </div>
        {/* Offcanvas Right */}
        <div
          className="offcanvas offcanvas-end"
          data-bs-scroll="true"
          data-bs-backdrop="false"
          tabIndex="-1"
          id="offcanvasScrolling"
          aria-labelledby="offcanvasScrollingLabel"
        >
          <SlideListSidebar
            slides={slides}
            currentSlide={currentSlide}
            submittedAnswers={submittedAnswers}
            onSlideSelect={handleSlideNavigation}
          />
        </div>
      </div>
      {/* Offcanvas Left */}
      <div
        className="offcanvas offcanvas-start"
        data-bs-scroll="true"
        data-bs-backdrop="true"
        tabIndex="-1"
        id="offcanvasLeft"
        aria-labelledby="offcanvasLeftLabel"
        style={{padding: 0}}
      >
        <ContentSidebar
          courseType={courseType}
          currentSectionNumber={sectionNumber}
          onNavigate={navigateTo}
          onClose={handleCloseLeftSidebar}
        />
      </div>
      <ContentFooter
        sectionId={foundSection?.section_id}
        courseType={courseType}
        isComplete={isLastSlide}
        chevronDirection={chevronDirection}
        onChevronClick={toggleRightSidebar}
        onMenuClick={toggleLeftSidebar}
        contextTitle={contextTitle}
        contextSection={contextSection}
        onComplete={handleSectionComplete}
      />
    </div>
  );
}

export default ContentPage;